import React, { useEffect, useMemo, useState } from 'react';
import { KeyRound, ShieldCheck, ShieldOff, Search, LifeBuoy, Check, Lock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { PageHeader, Card, Avatar } from './shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const ROLE_LABEL = { customer: 'Cliente', driver: 'Conductor', restaurant: 'Negocio', admin: 'Administrador' };
const ROLE_PLURAL = { customer: 'Clientes', driver: 'Conductores', restaurant: 'Negocios' };

const AdminAccess = () => {
  const { customers, drivers, restaurants, admin, adminSetPassword, adminDisableAccount, adminDeleteCustomer, changePassword, resetRequests, resolveResetRequest } = useApp();
  const [tab, setTab] = useState('driver');
  const [q, setQ] = useState('');
  const [target, setTarget] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [newPass, setNewPass] = useState('');
  const [requests, setRequests] = useState([]);
  const [own, setOwn] = useState({ current: '', next: '', confirm: '' });

  const loadRequests = () => resetRequests().then(setRequests).catch(() => setRequests([]));
  useEffect(() => { loadRequests(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const rows = useMemo(() => {
    const source = tab === 'driver' ? drivers : tab === 'restaurant' ? restaurants : customers;
    const list = (source || []).map((x) => ({
      id: x.id, name: x.name, sub: tab === 'restaurant' ? x.email : x.phone,
      avatar: x.avatar || x.logo, disabled: !!x.accountDisabled,
    }));
    const needle = q.trim().toLowerCase();
    return needle ? list.filter((r) => `${r.name} ${r.sub}`.toLowerCase().includes(needle)) : list;
  }, [tab, drivers, restaurants, customers, q]);

  const savePassword = async () => {
    if (newPass.length < 6) return toast.error('La contraseña debe tener al menos 6 caracteres');
    await adminSetPassword(tab, target.id, newPass);
    setTarget(null); setNewPass(''); loadRequests();
  };

  const saveOwn = async () => {
    if (own.next.length < 6) return toast.error('La nueva contraseña debe tener al menos 6 caracteres');
    if (own.next !== own.confirm) return toast.error('Las contraseñas no coinciden');
    await changePassword(own.current, own.next);
    setOwn({ current: '', next: '', confirm: '' });
  };

  return (
    <div>
      <PageHeader title="Accesos y contraseñas" subtitle="Restablece contraseñas, desactiva cuentas y atiende solicitudes de recuperación" />

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
        <Card className="xl:col-span-3">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {['driver', 'restaurant', 'customer'].map((r) => (
              <button key={r} data-testid={`access-tab-${r}`} onClick={() => { setTab(r); setQ(''); }} className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${tab === r ? 'bg-[#1E2130] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {ROLE_PLURAL[r]}
              </button>
            ))}
            <div className="ml-auto relative"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><Input data-testid="access-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nombre, teléfono o correo" className="h-9 pl-9 w-[260px]" /></div>
          </div>

          <ul className="divide-y divide-gray-100" data-testid="access-list">
            {rows.length === 0 && <li className="text-center text-gray-400 py-12 text-sm">No hay cuentas de este tipo todavía</li>}
            {rows.map((r) => (
              <li key={r.id} data-testid={`access-row-${r.id}`} className="flex flex-wrap items-center gap-2 py-3">
                <Avatar src={r.avatar} name={r.name} size={36} />
                <div className="flex-1 min-w-[140px]">
                  <p className="font-semibold text-sm truncate flex items-center gap-2">{r.name}
                    {r.disabled && <span className="text-[10px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Desactivada</span>}
                  </p>
                  <p className="text-[11px] text-gray-500 truncate">{r.sub}</p>
                </div>
                <Button data-testid={`access-reset-${r.id}`} variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={() => { setTarget(r); setNewPass(''); }}><KeyRound size={14} /> Contraseña</Button>
                <Button data-testid={`access-toggle-${r.id}`} size="sm" variant={r.disabled ? 'default' : 'outline'} className={`gap-1.5 shrink-0 ${r.disabled ? 'bg-emerald-600 hover:bg-emerald-700' : 'text-red-600 border-red-200 hover:bg-red-50'}`}
                  onClick={() => adminDisableAccount(tab, r.id, !r.disabled)}>
                  {r.disabled ? <><ShieldCheck size={14} /> Activar</> : <><ShieldOff size={14} /> Desactivar</>}
                </Button>
                {tab === 'customer' && (
                  <Button data-testid={`access-delete-${r.id}`} size="sm" variant="outline" className="gap-1.5 shrink-0 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setToDelete(r)}>
                    <Trash2 size={14} /> Eliminar
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Card>

        <div className="xl:col-span-2 space-y-5">
          <Card title="Mi contraseña de administrador">
            <div className="px-1 pb-1 space-y-3">
              <p className="text-xs text-gray-500">Sesión activa: <span className="font-semibold text-[#1E2130]">{admin?.email}</span></p>
              <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Contraseña actual</Label><Input data-testid="own-current" type="password" value={own.current} onChange={(e) => setOwn({ ...own, current: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Nueva contraseña</Label><Input data-testid="own-next" type="password" value={own.next} onChange={(e) => setOwn({ ...own, next: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Confirmar</Label><Input data-testid="own-confirm" type="password" value={own.confirm} onChange={(e) => setOwn({ ...own, confirm: e.target.value })} /></div>
              <Button data-testid="own-save" onClick={saveOwn} className="w-full bg-[#EE3B36] hover:bg-[#d43530] gap-2"><Lock size={15} /> Cambiar mi contraseña</Button>
            </div>
          </Card>

          <Card title="Solicitudes de recuperación">
            <ul className="divide-y divide-gray-100" data-testid="reset-requests">
              {requests.length === 0 && <li className="text-center text-gray-400 py-10 text-sm">Sin solicitudes pendientes</li>}
              {requests.map((r) => (
                <li key={r.id} data-testid={`reset-request-${r.id}`} className="flex items-start gap-3 py-3">
                  <span className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"><LifeBuoy size={17} /></span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{r.name || r.identifier}</p>
                    <p className="text-[11px] text-gray-500">{ROLE_LABEL[r.role]} · {r.identifier} · {r.matched ? 'cuenta encontrada' : 'sin coincidencia'}</p>
                  </div>
                  <button data-testid={`reset-resolve-${r.id}`} onClick={() => resolveResetRequest(r.id).then(loadRequests)} className="w-8 h-8 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 flex items-center justify-center shrink-0"><Check size={16} /></button>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nueva contraseña para {target?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-gray-600">Contraseña ({ROLE_LABEL[tab]})</Label>
            <Input data-testid="access-new-password" value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="Mínimo 6 caracteres" />
            <p className="text-[11px] text-gray-500">Comparte esta contraseña por WhatsApp. El usuario podrá cambiarla al ingresar.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>Cancelar</Button>
            <Button data-testid="access-save-password" className="bg-[#EE3B36] hover:bg-[#d43530]" onClick={savePassword}>Guardar contraseña</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent data-testid="access-delete-dialog">
          <DialogHeader><DialogTitle>Eliminar la cuenta de {toDelete?.name}</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-600 space-y-2">
            <p>Se borran la cuenta, el teléfono, el correo, las direcciones, la foto y los favoritos de este cliente. No se puede deshacer.</p>
            <p>Sus pedidos ya cobrados se conservan para la contabilidad, pero aparecerán como <b>«Cuenta eliminada»</b> sin nombre ni dirección.</p>
            <p className="text-[12px] text-gray-500">Usa esto solo cuando el cliente lo pida por WhatsApp o correo; desde la app el cliente puede eliminarla él mismo.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToDelete(null)}>Cancelar</Button>
            <Button data-testid="access-confirm-delete" className="bg-[#EE3B36] hover:bg-[#d43530]" onClick={() => adminDeleteCustomer(toDelete.id).then(() => setToDelete(null))}>Eliminar definitivamente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAccess;
